import React, { useState, useEffect, useRef } from 'react';
import KpiIcons from './KpiIcons';
import { useDebounce } from '../hooks/useDebounce';

interface SearchableDropdownProps {
  options: Array<{ id: string; name: string; disabled?: boolean; description?: string; kpis?: string[] }>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  className?: string;
  grouped?: boolean;
  width?: string;
  ariaLabel?: string;
  loading?: boolean;
  emptyStateMessage?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  className = "",
  grouped = false,
  width = "320px",
  ariaLabel,
  loading = false,
  emptyStateMessage = "Inga resultat hittades"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search term to improve performance with large datasets
  const debouncedSearchTerm = useDebounce(searchTerm, 150);

  // Filter and group options based on debounced search term
  useEffect(() => {
    if (!debouncedSearchTerm) {
      setFilteredOptions(options);
      setSelectedOptionId(null);
      return;
    }

    const filtered = options.filter(option =>
      option.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
    setSelectedOptionId(filtered.length > 0 ? filtered[0].id : null);
  }, [debouncedSearchTerm, options]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const currentIndex = filteredOptions.findIndex(opt => opt.id === selectedOptionId);
        const nextIndex = currentIndex < filteredOptions.length - 1 ? currentIndex + 1 : 0;
        setSelectedOptionId(filteredOptions[nextIndex]?.id || null);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const currentIndexUp = filteredOptions.findIndex(opt => opt.id === selectedOptionId);
        const prevIndex = currentIndexUp > 0 ? currentIndexUp - 1 : filteredOptions.length - 1;
        setSelectedOptionId(filteredOptions[prevIndex]?.id || null);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedOptionId) {
          const selectedOption = filteredOptions.find(opt => opt.id === selectedOptionId);
          if (selectedOption && !selectedOption.disabled) {
            handleSelect(selectedOptionId);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setSelectedOptionId(null);
        break;
    }
  };

  // Group options for better organization
  const getGroupedOptions = () => {
    if (!grouped) return { all: filteredOptions };

    const withData = filteredOptions.filter(option => !option.disabled);
    const withoutData = filteredOptions.filter(option => option.disabled);

    return { withData, withoutData };
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      // On mobile, ensure the input is visible when the keyboard comes up
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isOpen]);

  const selectedOption = options.find(option => option.id === value);

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  return (
    <div className={`relative ${className}`} style={{ minWidth: width }} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled || loading}
        className={`w-full text-left text-xs rounded border border-gray-300 bg-white px-2 py-1 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500 flex items-center justify-between dark:bg-card dark:border-slate-600 dark:text-foreground`}
        aria-label={ariaLabel || placeholder}
      >
        <span className="truncate">
          {loading ? 'Laddar...' : (selectedOption ? selectedOption.name : placeholder)}
        </span>
        {loading ? (
          <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ) : (
          <svg
            className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded shadow-lg overflow-hidden dark:bg-card dark:border-slate-600"
          style={{
            maxHeight: 'calc(var(--vh, 1vh) * 80 - var(--kb, 0px))',
            bottom: 'auto'
          }}
          role="listbox"
          aria-label={`${ariaLabel || placeholder} alternativ`}
          aria-expanded="true"
        >
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 dark:border-slate-700">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Sök ${placeholder.toLowerCase()}...`}
              className="w-full text-sm px-3 py-2 border border-gray-300 rounded focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:bg-card dark:border-slate-600 dark:text-foreground"
              aria-label={`Sök ${ariaLabel || placeholder}`}
              aria-describedby={`${ariaLabel || placeholder}-search-help`}
              aria-autocomplete="list"
              aria-activedescendant={selectedOptionId || undefined}
            />
            <div id={`${ariaLabel || placeholder}-search-help`} className="sr-only">
              Använd piltangenterna för att navigera genom alternativen. Tryck på Enter för att välja. Tryck på Escape för att stänga.
            </div>
          </div>

          {/* ARIA Live Region for announcements */}
          <div
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            id={`${ariaLabel || placeholder}-announcements`}
          >
            {filteredOptions.length > 0
              ? `${filteredOptions.length} alternativ tillgängliga`
              : 'Inga alternativ hittades'
            }
          </div>

          {/* Options List */}
          <div
            className="max-h-64 overflow-y-auto"
            role="listbox"
            aria-label={`${ariaLabel || placeholder} alternativ`}
          >
            {filteredOptions.length > 0 ? (
              (() => {
                const grouped = getGroupedOptions();

                if (grouped.all) {
                  // Simple list (no grouping)
                  return grouped.all.map((option) => (
                    <OptionItem
                      key={option.id}
                      option={option}
                      value={selectedOptionId || value}
                      onSelect={handleSelect}
                    />
                  ));
                } else {
                  // Grouped list
                  return (
                    <>
                      {grouped.withData && grouped.withData.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">
                            Skolor med data
                          </div>
                          {grouped.withData.map((option) => (
                            <OptionItem
                              key={option.id}
                              option={option}
                              value={selectedOptionId || value}
                              onSelect={handleSelect}
                            />
                          ))}
                        </>
                      )}

                      {grouped.withoutData && grouped.withoutData.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">
                            Skolor utan data
                          </div>
                          {grouped.withoutData.map((option) => (
                            <OptionItem
                              key={option.id}
                              option={option}
                              value={selectedOptionId || value}
                              onSelect={handleSelect}
                            />
                          ))}
                        </>
                      )}
                    </>
                  );
                }
              })()
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400">
                {emptyStateMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Option Item Component
interface OptionItemProps {
  option: { id: string; name: string; disabled?: boolean; description?: string; kpis?: string[] };
  value: string;
  onSelect: (id: string) => void;
}

const OptionItem: React.FC<OptionItemProps> = ({ option, value, onSelect }) => {
  const isSelected = option.id === value;
  const isDisabled = option.disabled;

  return (
    <button
      type="button"
      onClick={() => !isDisabled && onSelect(option.id)}
      disabled={isDisabled}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      className={`w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors dark:hover:bg-slate-800 dark:focus:bg-slate-800 ${isSelected ? 'bg-emerald-50 text-emerald-700 dark:bg-slate-800 dark:text-emerald-400' : 'text-gray-900 dark:text-foreground'
        } ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      title={isDisabled ? 'Ingen data tillgänglig' : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${isDisabled ? 'text-gray-500 dark:text-slate-400' : ''}`}>
            {option.name}
          </div>
          {option.description && (
            <div className="text-xs text-gray-400 mt-0.5 dark:text-slate-500">
              {option.description}
            </div>
          )}
        </div>
        <div className="ml-2 flex items-center gap-2 flex-shrink-0">
          {option.kpis && !isDisabled && (
            <KpiIcons kpis={option.kpis} size="sm" />
          )}
          {isDisabled && (
            <svg className="w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          )}
        </div>
      </div>
    </button>
  );
};

export default SearchableDropdown;
